import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  apiGetPatientsPatientIdPpgs,
  PermissionBit,
  QuickLookTasks,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { QuickLookButton } from "../../../components/quick-look/quick-look-button";
import { AddButton } from "../../../components/ui-kit/add-button";
import { NativeSelect } from "../../../components/ui-kit/base/native-select";
import { Button } from "../../../components/ui-kit/button";
import { CopyButton } from "../../../components/ui-kit/copy-button";
import { CellJSON } from "../../../components/ui-kit/tables/cells/cell-json";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { InfoTable } from "../../../components/ui-kit/tables/info-table";
import {
  ppgConditionLabels,
  ppgMeasModes,
  ppgQuality,
} from "../../../constants/enum-to-text";
import { hasPermission } from "../../../constants/permissions";
import { PPGQuickLook } from "../../../models/quick-look-analyses";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { useFetch } from "../../../utils/use-fetch";
import { PatientSubpage } from "../common/patient-subpage";
import { PPGShareDialog } from "./ppg-share-dialog";
import { PPGs } from "./ppgs";

const idParam = "uuid";

export const PPGTable = observer(function PPGTable(props: { app: AppModel }) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [searchParams, setSearchParams] = useSearchParams();

  const [model] = useState(
    () => new PPGs([], api.permissions, api.requireUserUUID()),
  );
  const [ppgQuickLook] = useState(() => new PPGQuickLook(api, null, []));

  const canQuickLook = hasPermission(api.permissions, PermissionBit.PPG_RAW);

  const handleQuickLookSelect = useCallback(
    (selected: unknown[]) => {
      ppgQuickLook.uuids = selected as string[];
      ppgQuickLook.reset();
    },
    [ppgQuickLook],
  );

  useEffect(() => {
    const uuids = searchParams.getAll(idParam);
    model.setIdsFilter(uuids.length ? uuids : undefined);
  }, [searchParams, model]);

  const { loading, refetch } = useFetch(
    async ({ isRefetch }) => {
      const response = await apiGetPatientsPatientIdPpgs({
        path: { patient_id: patient.patientId },
        meta: { useSessionCache: !isRefetch },
      });
      model.updateData(response.data.ppg_data);
    },
    [patient.patientId],
  );

  return (
    <PatientSubpage title={t`PPG data`} loading={loading}>
      <PPGShareDialog ppgQuickLook={ppgQuickLook} />

      {model.idsFilter && (
        <div className="flex flex-col items-center">
          <div className="text-brand-primary font-bold">
            <Trans>Filtering by IDs is active.</Trans>
          </div>

          <div className="h-1" />

          <Button
            onClick={() => {
              setSearchParams({});
            }}
          >
            <Trans>Clear filter</Trans>
          </Button>

          <div className="h-4" />
        </div>
      )}

      <DataTable
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        canSelect={canQuickLook ? "multi" : undefined}
        selectionHint={t`Select recordings to run a QuickLook analysis.`}
        onSelect={handleQuickLookSelect}
        filterNoun={(count) =>
          t`${plural(count, { one: "recording", other: "recordings" })}`
        }
        filterActions={
          <div className="flex items-center gap-1">
            <AddButton href="add">
              <Trans>Add PPG data</Trans>
            </AddButton>
            {canQuickLook && (
              <>
                <NativeSelect
                  className="w-auto"
                  value={ppgQuickLook.analysisType ?? ""}
                  onChange={(event) => {
                    ppgQuickLook.analysisType = event.target
                      .value as QuickLookTasks;
                    ppgQuickLook.reset();
                  }}
                >
                  <option value="" disabled>
                    {t`Select type...`}
                  </option>
                  <option value={QuickLookTasks.ppg_timeseries_ql}>
                    {t`Basic QuickLook`}
                  </option>
                  <option value={QuickLookTasks.ppg_auc_annotated_ql}>
                    {t`AUC Annotated QuickLook`}
                  </option>
                </NativeSelect>
                <QuickLookButton
                  size="small"
                  label={t`Submit QuickLook`}
                  resultLabel={t`View Analysis`}
                  model={ppgQuickLook}
                  disabled={
                    ppgQuickLook.uuids.length === 0 ||
                    !ppgQuickLook.analysisType ||
                    !patient.consentOk()
                  }
                />
                {ppgQuickLook.resultUrl && (
                  <Button
                    onClick={async () => {
                      await ppgQuickLook.shareQuickLook();
                    }}
                  >
                    <Trans>Share analysis...</Trans>
                  </Button>
                )}
              </>
            )}
          </div>
        }
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Condition</Trans>,
            attr: "measurement_condition",
            type: "enum",
            editable: true,
            values: ppgConditionLabels,
            filter: {
              kind: "multi",
              group: t`PPG`,
              options: new Map(Object.entries(ppgConditionLabels)),
            },
          },
          {
            header: <Trans>Device</Trans>,
            attr: "measurement_device",
            type: "string",
            filter: {
              kind: "multi",
              group: t`PPG`,
              options: uniqueValueOptions("measurement_device"),
            },
          },
          {
            header: <Trans>Origin</Trans>,
            attr: "measurement_origin",
            type: "string",
            filter: {
              kind: "multi",
              group: t`PPG`,
              options: uniqueValueOptions("measurement_origin"),
            },
          },
          {
            header: <Trans>Length</Trans>,
            attr: "length",
            type: "number",
            precision: 1,
          },
          {
            header: <Trans>Mode</Trans>,
            attr: "measurement_mode",
            type: "enum",
            editable: true,
            values: ppgMeasModes,
            filter: {
              kind: "multi",
              group: t`PPG`,
              options: new Map(Object.entries(ppgMeasModes)),
            },
          },
          {
            header: <Trans>Quality</Trans>,
            attr: "quality_flag",
            type: "enum",
            editable: true,
            values: ppgQuality,
            filter: {
              kind: "multi",
              group: t`PPG`,
              options: new Map(Object.entries(ppgQuality)),
            },
          },
          {
            header: <Trans>SQI</Trans>,
            attr: "computed_sqi",
            type: "number",
            precision: 1,
          },
          {
            header: <Trans>Battery [%]</Trans>,
            attr: "deviceBattery",
            type: "number",
            precision: 0,
          },
          {
            header: <Trans>Measurement status</Trans>,
            cell: ({ item }) => <CellJSON value={item.statusPercentage} />,
          },
          api.isProdEnv && {
            header: <Trans>LabelStudio</Trans>,
            cell: ({ item }) => (
              <Button
                onClick={async () => {
                  await patient.redirectToLabelStudio(item.uuid);
                }}
              >
                <Trans>Redirect</Trans>
              </Button>
            ),
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            editable: true,
            multiline: 3,
            width: 200,
          },
          {
            header: <Trans>Copy UUID</Trans>,
            width: 60,
            sticky: "right",
            cell: ({ item }) => <CopyButton value={item.uuid} variant="icon" />,
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("ppg", item.uuid, editedField);
          await refetch();
        }}
      />

      <div className="flex flex-col items-start">
        <h4 className="mt-12 mb-2 font-bold">
          <Trans>Note for PPG quality flags:</Trans>
        </h4>
        <InfoTable>
          <tr>
            <td>
              <strong>
                <Trans>Data error:</Trans>
              </strong>
            </td>
            <td>
              <Trans>
                Completely bad signal, should not be used under any
                circumstances; this flag is marked automatically when computed{" "}
                <strong>SQI is &lt;= 3.0</strong>;<br className="h-5" />
                <span className="text-danger-foreground">
                  manual changes to the PPG flag to <em>data error</em> will
                  cause all related data points (Seerlinq-computed data and
                  PPG-derived) to change their flag to <em>Wrong</em>
                </span>
                ;<br className="h-5" />
                PPG data with <em>data error</em> flag are{" "}
                <strong>not used in any automatic algorithms</strong>
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                <Trans>OK:</Trans>
              </strong>
            </td>
            <td>
              <Trans>Signal is OK to use for analyses</Trans>
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                <Trans>Low signal quality:</Trans>
              </strong>
            </td>
            <td>
              <Trans>
                Signal is not perfect, some weirdness happening, need manual
                check / preprocessing; this flag is marked automatically when
                computed <strong>SQI is between 3.0 and 5.0</strong>;
                <br className="h-5" />
                <span className="text-danger-foreground">
                  manual changes to the PPG flag to <em>low signal quality</em>{" "}
                  will cause all related data points (Seerlinq-computed data and
                  PPG-derived) to change their flag to <em>Low PPG Quality</em>
                </span>
                ;<br className="h-5" />
                PPG data with <em>low signal quality</em> flag are used in
                automatic algorithms,{" "}
                <strong>
                  but the resulting Seerlinq-computed data instance (like DRI)
                  automatically inherits the <em>Low PPG Quality</em> flag.
                </strong>
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                <Trans>Artefacts:</Trans>
              </strong>
            </td>
            <td>
              <Trans>
                Signal contains a lot of artefacts, it is not OK to use as is
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                <Trans>Short:</Trans>
              </strong>
            </td>
            <td>
              <Trans>
                Signal is short, maybe recording by accident, will not be used
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                <Trans>Extra:</Trans>
              </strong>
            </td>
            <td>
              <Trans>
                Additional circumstance that may be important, data will be
                treated with care, please put additional info to the comment
                (e.g., gel nails, or pacemaker)
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <strong>
                <Trans>Testing:</Trans>
              </strong>
            </td>
            <td>
              <Trans>
                Signal is used for testing, will not be used or included in any
                analysis
              </Trans>
            </td>
          </tr>
        </InfoTable>
      </div>
    </PatientSubpage>
  );
});
